import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme, darken } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Link, useNavigate } from 'react-router-dom';
import courseService, { CourseType, getEligibleWebinarInstances } from '../services/courseService';
import BookmarkButton from '../components/BookmarkButton';
import { useDebounce } from '../hooks/useDebounce';
import type { Course } from '../services/courseService';
import { useUserContext } from '../hooks/useUserContext';
import addonAssignmentService, { type AddonAccessCheck, type AddonSeatSummary, ADDON_DISPLAY_NAMES } from '../services/addonAssignmentService';
import tenantService from '../services/tenantService';
import registrationService, {
  type Registration,
  isRegistrationActive,
} from '../services/registrationService';
import { getApiErrorInfo } from '../services/api';
import { track } from '../services/analytics';
import { parseApiDateAsUtc } from '../services/webinarTiming';
import cpeHistoryService from '../services/cpeHistoryService';
import { getCategoryIcon, getFieldsOfStudy } from '../utils/fieldOfStudy';

type TypeFilter = 'all' | 'ondemand' | 'webinar' | 'conference';

const PAGE_SIZE = 25;
const MY_COURSES_INITIAL_VISIBLE = 5;

function parseApiDate(dateStr: string): Date {
  return parseApiDateAsUtc(dateStr);
}

function formatTime(dateStr: string, includeTz = false) {
  return parseApiDate(dateStr).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(includeTz ? { timeZoneName: 'short', timeZone: 'America/New_York' } : {}),
  });
}

function formatWebinarInstance(startDate?: string, endDate?: string): string {
  if (!startDate) return '';
  const date = parseApiDate(startDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const start = formatTime(startDate);
  if (endDate) {
    return `${date} · ${start}–${formatTime(endDate, true)}`;
  }
  return `${date} · ${formatTime(startDate, true)}`;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}

const INSTRUCTOR_CREDENTIALS = new Set([
  'cpa', 'phd', 'ph', 'd', 'cff', 'cgma', 'cma', 'jd', 'esq', 'mba', 'ms', 'ma', 'ea',
  'cfe', 'cisa', 'cia', 'cfp', 'pa', 'ca', 'llm', 'ctrs', 'abc',
]);

function normalizeCredentialToken(token: string): string {
  return token.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isCredentialOnlyLabel(name: string): boolean {
  const tokens = name
    .split(/\s+/)
    .map(normalizeCredentialToken)
    .filter(Boolean);
  return tokens.length > 0 && tokens.every((t) => INSTRUCTOR_CREDENTIALS.has(t));
}

function stripInstructorCredentials(name: string): string {
  // Remove common trailing credentials so equivalent names collapse together.
  const credentialPattern =
    /(?:,?\s+(?:ph\.?d\.?|cpa|mba|m\.?s\.?|m\.a\.?|j\.?d\.?|esq\.?|cfe|cisa|cia|cma|ea|cfp)(?:\s*\([^)]*\))?)+\.?\s*$/i;
  return name.replace(credentialPattern, '').trim();
}

function cleanInstructorSegment(raw: string): string {
  let value = raw.trim();
  if (!value) return '';

  // Remove known trailing source/provider tags (e.g., " - TKI").
  value = value.replace(/\s*-\s*[A-Z]{2,6}\s*$/g, '');
  // Remove short trailing aliases in parentheses (e.g., "(HAL)", "(TD)").
  value = value.replace(/\s*\([A-Z]{2,6}\)\s*$/g, '');
  value = stripInstructorCredentials(value);
  value = value.replace(/\s+/g, ' ').trim();

  if (!value || isCredentialOnlyLabel(value)) return '';
  return value;
}

function extractInstructorNames(rawName: string): string[] {
  // Split multi-instructor labels while preserving hyphenated last names.
  const parts = rawName.split(/\s*(?:\/|;|&|\band\b)\s*/i);
  const cleaned = parts
    .map(cleanInstructorSegment)
    .filter(Boolean);

  return cleaned.length > 0 ? cleaned : [];
}

function canonicalizeInstructorName(name: string): string {
  const cleaned = cleanInstructorSegment(name);
  const base = cleaned || name;
  const normalized = base
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Ignore single-letter tokens (middle initials) for matching/deduping.
  const tokens = normalized.split(' ').filter(Boolean);
  const withoutInitials = tokens.filter((t) => t.length > 1);
  return (withoutInitials.length >= 2 ? withoutInitials : tokens).join(' ');
}

function formatCreditLabel(course: Course): string {
  const hours = course.creditHours;
  const categories = [...new Set((course.courseCredits ?? []).map(c => c.educationCategoryName).filter(Boolean))];
  const label = `${hours} CPE Credit${hours !== 1 ? 's' : ''}`;
  return categories.length > 0 ? `${label} · ${categories.join(', ')}` : label;
}

function getRegistrationDetailsPath(reg: Registration): string {
  return `${reg.courseId}`;
}

function getRegistrationSortKey(reg: Registration): number {
  if (reg.startDate) {
    const start = parseApiDate(reg.startDate).getTime();
    // Future or joinable: sort ascending (soonest first)
    if (isRegistrationActive(reg)) return start;
    // Past: sort after all future, descending (most recent first)
    return Number.MAX_SAFE_INTEGER - start;
  }
  // On-demand with no date: after all dated courses
  return Number.MAX_SAFE_INTEGER - 1;
}

const INTERLEAVE_INTERVALS = [4, 3, 5, 4, 3];

function buildInterleavedList(courses: Course[], from: Date | null, to: Date | null): Course[] {
  const webinars = courses
    .filter(c => c.courseType === CourseType.Webinar || c.courseType === CourseType.Conference)
    .sort((a, b) => {
      const dateA = getEligibleWebinarInstances(a.upcomingInstances, from, to)[0]?.startDate ?? a.startDate ?? '';
      const dateB = getEligibleWebinarInstances(b.upcomingInstances, from, to)[0]?.startDate ?? b.startDate ?? '';
      return dateA.localeCompare(dateB);
    });
  const onDemand = courses.filter(c => c.courseType === CourseType.OnDemand);

  if (webinars.length === 0) return onDemand;
  if (onDemand.length === 0) return webinars;

  const result: Course[] = [];
  let webinarIdx = 0;
  let countSinceLast = 0;
  let intervalIdx = 0;

  for (const course of onDemand) {
    result.push(course);
    countSinceLast++;
    const threshold = INTERLEAVE_INTERVALS[intervalIdx % INTERLEAVE_INTERVALS.length];
    if (countSinceLast >= threshold && webinarIdx < webinars.length) {
      result.push(webinars[webinarIdx++]);
      countSinceLast = 0;
      intervalIdx++;
    }
  }

  while (webinarIdx < webinars.length) {
    result.push(webinars[webinarIdx++]);
  }

  return result;
}

// Segmented pill control for the My Courses section.
// Mirrors the established pattern from MyLearning.tsx: tinted track,
// white selected pill with subtle shadow, count alongside each label.
function SegmentedFilter<T extends string>(props: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string; count?: number }>;
  onChange: (value: T) => void;
  showCounts?: boolean;
}) {
  const { label, value, options, onChange, showCounts = true } = props;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '10.5px',
          fontWeight: 600,
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          lineHeight: 1,
          ml: '6px',
        }}
      >
        {label}
      </Typography>
      <Box
        role="radiogroup"
        aria-label={label}
        sx={(theme) => ({
          display: 'inline-flex',
          p: '4px',
          bgcolor: theme.palette.tint.neutralMuted,
          borderRadius: '10px',
          width: 'fit-content',
        })}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <Box
              key={opt.value}
              component="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                px: '10px',
                py: '5px',
                borderRadius: '7px',
                border: 'none',
                cursor: 'pointer',
                bgcolor: selected ? theme.palette.primary.main : 'transparent',
                boxShadow: selected
                  ? '0 1px 3px rgba(67, 122, 43, 0.35), 0 0 0 0.5px rgba(67, 122, 43, 0.18)'
                  : 'none',
                transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
                outline: 'none',
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              })}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '13px',
                  fontWeight: selected ? 600 : 400,
                  color: selected ? 'primary.contrastText' : 'text.secondary',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                {opt.label}
              </Typography>
              {showCounts && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: selected ? 'rgba(255, 255, 255, 0.7)' : 'text.disabled',
                    lineHeight: 1,
                    userSelect: 'none',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {opt.count}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

type MyCoursesStatusFilter = 'active' | 'completed' | 'all';

export default function Courses() {
  const navigate = useNavigate();
  const { backendProfile, isTenantAdmin } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [recommendedTypeFilter, setRecommendedTypeFilter] = useState<TypeFilter>('all');
  const [creditFilters, setCreditFilters] = useState<number[]>([]);
  const [levelFilter, setLevelFilter] = useState('');
  const [instructorFilter, setInstructorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState<'today' | 'tomorrow' | 'next-week' | null>(null);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchQuery, 300);

  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [myCoursesTypeFilter, setMyCoursesTypeFilter] = useState<TypeFilter>('all');
  const [myCoursesStatusFilter, setMyCoursesStatusFilter] = useState<MyCoursesStatusFilter>('active');
  const [showAllMyCourses, setShowAllMyCourses] = useState(false);

  const [serverRecommended, setServerRecommended] = useState<Course[]>([]);
  const [educationCategories, setEducationCategories] = useState<string[]>([]);
  const [accessChecks, setAccessChecks] = useState<Record<number, AddonAccessCheck | undefined>>({});

  const [seatSummary, setSeatSummary] = useState<AddonSeatSummary[] | null>(null);
  const [activatingAddon, setActivatingAddon] = useState<string | null>(null);

  const [requestDialog, setRequestDialog] = useState<{ courseType: 0 | 1 | 2; addonId?: string } | null>(null);
  const [requestNote, setRequestNote] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState<string | null>(null);

  // Initial load: fetch all courses from the Meilisearch index
  useEffect(() => {
    setLoading(true);
    courseService.searchCourses({}).then((data) => {
      setAllCourses(data);
      setCourses(data);
      setLoading(false);
    });

    setRecommendedLoading(true);
    courseService.getRecommendedCourses(10)
      .then(setServerRecommended)
      .catch(() => setServerRecommended([]))
      .finally(() => setRecommendedLoading(false));

    cpeHistoryService.getEducationCategories()
      .then((cats) => setEducationCategories(cats.map((c) => c.name)))
      .catch(() => {});
    checkAllAccess();
    registrationService.getUserRegistrations().then(setRegistrations);
    if (isTenantAdmin && backendProfile?.tenantId) {
      addonAssignmentService.getSeatSummary(backendProfile.tenantId).then(setSeatSummary).catch(() => {});
    }
  }, []);

  // Reactive text search via Meilisearch
  useEffect(() => {
    if (!debouncedSearch) {
      setCourses(allCourses);
      return;
    }
    courseService.searchCourses({ searchTerm: debouncedSearch })
      .then(results => {
        setCourses(results);
        track('course_searched', { query: debouncedSearch, result_count: results.length });
      })
      .catch(() => setCourses(allCourses));
  }, [debouncedSearch, allCourses]);

  const checkAllAccess = async () => {
    try {
      const [check0, check1] = await Promise.all([
        addonAssignmentService.checkAddonAccess(0),
        addonAssignmentService.checkAddonAccess(1),
      ]);
      setAccessChecks({ 0: check0, 1: check1 });
    } catch {
      setAccessChecks({ 0: undefined, 1: undefined });
    }
  };

  const handleRequestAccess = async () => {
    if (!requestDialog) return;
    const tenantId = backendProfile?.tenantId || await tenantService.determineTenantId().catch(() => null);
    if (!tenantId) {
      setRequestError('Could not determine your tenant. Please try again.');
      return;
    }
    const addonId = requestDialog.addonId;
    if (!addonId) {
      setRequestError('No module found for this course type.');
      return;
    }
    try {
      setRequestLoading(true);
      setRequestError(null);
      await addonAssignmentService.requestAccess(tenantId, addonId, requestNote || undefined);
      setRequestSuccess('Access request submitted. Your admin will review it shortly.');
      setAccessChecks(prev => ({
        ...prev,
        [requestDialog.courseType]: prev[requestDialog.courseType]
          ? { ...prev[requestDialog.courseType]!, hasPendingRequest: true }
          : undefined,
      }));
      setTimeout(() => {
        setRequestDialog(null);
        setRequestSuccess(null);
        setRequestNote('');
      }, 2000);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setRequestError(apiError.message || 'Failed to submit request. Please try again.');
    } finally {
      setRequestLoading(false);
    }
  };

  // Number of catalog filter dimensions currently active. Drives the
  // visibility and label of the "Clear filters" action so it only appears
  // when there is actually something to clear.
  const activeFilterCount = [
    searchQuery !== '',
    selectedCategory !== 'All',
    typeFilter !== 'all',
    creditFilters.length > 0,
    levelFilter !== '',
    instructorFilter !== '',
    dateFrom !== '' || dateTo !== '',
  ].filter(Boolean).length;

  const handleResetFilters = () => {
    setSelectedCategory('All');
    setTypeFilter('all');
    setCreditFilters([]);
    setLevelFilter('');
    setInstructorFilter('');
    setDateFrom('');
    setDateTo('');
    setActiveDatePreset(null);
    setSearchQuery('');
  };

  const handleDatePreset = (preset: 'today' | 'tomorrow' | 'next-week') => {
    const today = new Date();
    // Build YYYY-MM-DD from local calendar parts so date inputs reflect the user's timezone.
    const fmt = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    if (preset === 'today') {
      setDateFrom(fmt(today));
      setDateTo(fmt(today));
    } else if (preset === 'tomorrow') {
      const tom = new Date(today);
      tom.setDate(today.getDate() + 1);
      setDateFrom(fmt(tom));
      setDateTo(fmt(tom));
    } else {
      // Always target the next calendar work week (Mon-Fri), regardless of current day.
      const start = new Date(today);
      const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
      const daysUntilNextMonday = ((8 - dayOfWeek) % 7) || 7;
      start.setDate(today.getDate() + daysUntilNextMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 4);
      setDateFrom(fmt(start));
      setDateTo(fmt(end));
    }
    setActiveDatePreset(preset);
  };

  const toggleCredit = (credit: number) => {
    setCreditFilters(prev =>
      prev.includes(credit) ? prev.filter(c => c !== credit) : [...prev, credit]
    );
  };

  const renderEnrollButton = (course: Course) => {
    const accessCourseType =
      course.courseType === CourseType.Conference ? CourseType.Webinar : course.courseType;
    const courseTypeNum = accessCourseType as unknown as 0 | 1;
    const check = accessChecks[courseTypeNum];
    const label = 'Register';

    if (check === undefined || check.hasAccess) {
      return (
        <Button
          component={Link}
          to={`${course.id}`}
          size="small"
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 2,
            py: '5px',
            border: '1.5px solid',
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: 'transparent',
            flexShrink: 0,
            letterSpacing: '0.01em',
            '&:hover': {
              bgcolor: 'primary.main',
              color: '#fff',
              borderColor: 'primary.main',
            },
            transition: 'all 0.16s ease',
          }}
        >
          {label}
        </Button>
      );
    }

    if (check.hasPendingRequest) {
      return (
        <Box
          sx={{
            borderRadius: '100px',
            fontSize: '12px',
            fontWeight: 500,
            px: 1.75,
            py: '5px',
            border: '1.5px solid',
            borderColor: 'rgba(0,0,0,0.15)',
            color: 'text.disabled',
            bgcolor: 'rgba(0,0,0,0.03)',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Access Requested
        </Box>
      );
    }

    // Individual users: show Upgrade button
    if (!check.isTenantGated) {
      return (
        <Button
          size="small"
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            px: 2,
            py: '5px',
            border: '1.5px solid',
            borderColor: 'primary.main',
            color: 'primary.contrastText',
            bgcolor: 'primary.main',
            flexShrink: 0,
            letterSpacing: '0.01em',
            '&:hover': {
              bgcolor: 'primary.dark',
              borderColor: 'primary.dark',
            },
            transition: 'all 0.16s ease',
          }}
          onClick={() => navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing')}
        >
          Upgrade Now
        </Button>
      );
    }

    // Tenant admin: show Activate Access or Add Seats
    if (isTenantAdmin && backendProfile?.tenantId && backendProfile?.id) {
      const addonEntry = seatSummary?.find(s => s.addonId === check.requiredAddonId);
      const seatsAvailable = addonEntry ? addonEntry.availableCount > 0 : false;

      if (activatingAddon === check.requiredAddonId) {
        return <CircularProgress size={20} />;
      }

      if (seatsAvailable) {
        return (
          <Button
            size="small"
            sx={{
              borderRadius: '100px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 2,
              py: '5px',
              border: '1.5px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'transparent',
              flexShrink: 0,
              letterSpacing: '0.01em',
              '&:hover': {
                bgcolor: 'primary.main',
                color: '#fff',
                borderColor: 'primary.main',
              },
              transition: 'all 0.16s ease',
            }}
            onClick={async () => {
              const addonId = check.requiredAddonId;
              if (!addonId) return;
              setActivatingAddon(addonId);
              try {
                await addonAssignmentService.assignSeat(backendProfile.tenantId!, backendProfile.id, addonId);
                await checkAllAccess();
                addonAssignmentService.getSeatSummary(backendProfile.tenantId!).then(setSeatSummary).catch(() => {});
              } catch {
                // Seat may have been taken — refresh state
                await checkAllAccess();
                addonAssignmentService.getSeatSummary(backendProfile.tenantId!).then(setSeatSummary).catch(() => {});
              } finally {
                setActivatingAddon(null);
              }
            }}
          >
            Activate Access
          </Button>
        );
      }

      // No seats available
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Button
            size="small"
            sx={{
              borderRadius: '100px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 2,
              py: '5px',
              border: '1.5px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'transparent',
              flexShrink: 0,
              letterSpacing: '0.01em',
              '&:hover': {
                bgcolor: 'primary.main',
                color: '#fff',
                borderColor: 'primary.main',
              },
              transition: 'all 0.16s ease',
            }}
            onClick={() => navigate('/app/tenant-admin/billing')}
          >
            Add Seats
          </Button>
          <Typography variant="caption" color="text.disabled" sx={{ pr: 0.5 }}>
            No available seats
          </Typography>
        </Box>
      );
    }

    // Tenant users: show Request Access button
    return (
      <Button
        size="small"
        sx={{
          borderRadius: '100px',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 600,
          px: 2,
          py: '5px',
          border: '1.5px solid',
          borderColor: 'primary.main',
          color: 'primary.main',
          bgcolor: 'transparent',
          flexShrink: 0,
          letterSpacing: '0.01em',
          '&:hover': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderColor: 'primary.main',
          },
          transition: 'all 0.16s ease',
        }}
        onClick={() => {
          setRequestDialog({ courseType: courseTypeNum, addonId: check.requiredAddonId });
          setRequestNote('');
          setRequestError(null);
        }}
      >
        Request Access
      </Button>
    );
  };

  // Official NASBA fields of study from the education_categories lookup table
  // (already sorted alphabetically by the API).
  const categories = useMemo(
    () => ['All', ...educationCategories],
    [educationCategories]
  );

const instructors = useMemo(() => {
    const groups = new Map<string, string>();
    allCourses.forEach(c => {
      (c.speakers ?? []).forEach(s => {
        const rawName = (s.displayName || s.name || '').trim();
        if (!rawName) return;
        extractInstructorNames(rawName).forEach((candidate) => {
          const key = canonicalizeInstructorName(candidate);
          if (!key) return;
          const existing = groups.get(key);
          if (!existing) {
            groups.set(key, candidate);
            return;
          }
          // Prefer the simpler/cleaner label when multiple variants map to the same person.
          if (candidate.length < existing.length) {
            groups.set(key, candidate);
          }
        });
      });
    });
    return Array.from(groups.values()).sort((a, b) => a.localeCompare(b));
  }, [allCourses]);

  const CORE_CREDIT_VALUES = [1, 2, 3, 4, 8];
  const availableCreditValues = useMemo(
    () => CORE_CREDIT_VALUES.filter(v => allCourses.some(c => c.creditHours === v)),
    [allCourses]
  );

  // Active date range parsed once from the local calendar inputs; shared by the
  // filter predicate, the webinar sort, and the instance chip rendering so they
  // always agree on which instances are "in range".
  const dateRange = useMemo(() => {
    const parseLocalDate = (s: string, endOfDay = false) => {
      const [y, mo, d] = s.split('-').map(Number);
      return endOfDay ? new Date(y, mo - 1, d, 23, 59, 59, 999) : new Date(y, mo - 1, d, 0, 0, 0, 0);
    };
    return {
      from: dateFrom ? parseLocalDate(dateFrom) : null,
      to: dateTo ? parseLocalDate(dateTo, true) : null,
    };
  }, [dateFrom, dateTo]);

  // Predicate that applies every catalog filter EXCEPT the format/type filter.
  // Shared between the result set and the per-format counts so they stay in sync.
  const matchesNonTypeFilters = useMemo(() => {
    const { from, to } = dateRange;

    return (course: Course): boolean => {
      const matchesCategory = selectedCategory === 'All' || getFieldsOfStudy(course).includes(selectedCategory);
      const matchesLevel = !levelFilter || course.level === levelFilter;
      const selectedInstructorKey = canonicalizeInstructorName(instructorFilter);
      const matchesInstructor = !instructorFilter || (course.speakers ?? []).some((s) => {
        const name = (s.displayName || s.name || '').trim();
        if (!name) return false;
        return extractInstructorNames(name).some(
          (candidate) => canonicalizeInstructorName(candidate) === selectedInstructorKey
        );
      });
      const matchesCredit = creditFilters.length === 0 || creditFilters.includes(course.creditHours);
      const matchesDate = (() => {
        if (!from && !to) return true;
        // On Demand courses are always available — exclude them when a date range is active
        if (course.courseType === CourseType.OnDemand) return false;
        // Match on the same displayable set the chips render from (eligible ∩ in range),
        // so a matching course is guaranteed to show at least one instance chip.
        if (course.upcomingInstances && course.upcomingInstances.length > 0) {
          return getEligibleWebinarInstances(course.upcomingInstances, from, to).length > 0;
        }
        // Fallback for externally-sourced webinars with no instance list.
        if (!course.startDate) return false;
        const d = parseApiDate(course.startDate);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })();
      return matchesCategory && matchesCredit && matchesDate && matchesLevel && matchesInstructor;
    };
  }, [selectedCategory, levelFilter, instructorFilter, creditFilters, dateRange]);

  const filteredCourses = useMemo(() => {
    setPage(1);
    return courses.filter((course) => {
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'ondemand' && course.courseType === CourseType.OnDemand) ||
        (typeFilter === 'webinar' && course.courseType === CourseType.Webinar) ||
        (typeFilter === 'conference' && course.courseType === CourseType.Conference);
      return matchesType && matchesNonTypeFilters(course);
    });
  }, [typeFilter, courses, matchesNonTypeFilters]);

  // Per-format counts — reflect every other active filter so users can see
  // how many results each format option will yield before clicking.
  const formatCounts = useMemo(() => {
    const baseSet = courses.filter(matchesNonTypeFilters);
    return {
      all: baseSet.length,
      ondemand: baseSet.filter(c => c.courseType === CourseType.OnDemand).length,
      webinar: baseSet.filter(c => c.courseType === CourseType.Webinar).length,
      conference: baseSet.filter(c => c.courseType === CourseType.Conference).length,
    };
  }, [courses, matchesNonTypeFilters]);

  const interleavedCourses = useMemo(
    () => typeFilter === 'all' ? buildInterleavedList(filteredCourses, dateRange.from, dateRange.to) : filteredCourses,
    [filteredCourses, typeFilter, dateRange]
  );

  const visibleCourses = interleavedCourses.slice(0, page * PAGE_SIZE);

  const hasMoreRef = useRef(false);
  hasMoreRef.current = visibleCourses.length < interleavedCourses.length;
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMoreRef.current) setPage(p => p + 1); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl]);

  const recommendedCourses = useMemo(() => {
    const MAX_RECOMMENDED = 5;

    if (recommendedTypeFilter !== 'all') {
      return serverRecommended
        .filter((course) =>
          (recommendedTypeFilter === 'ondemand' && course.courseType === CourseType.OnDemand) ||
          (recommendedTypeFilter === 'webinar' && course.courseType === CourseType.Webinar) ||
          (recommendedTypeFilter === 'conference' && course.courseType === CourseType.Conference)
        )
        .slice(0, MAX_RECOMMENDED);
    }

    const onDemand = serverRecommended.filter((course) => course.courseType === CourseType.OnDemand);
    const webinars = serverRecommended.filter((course) => course.courseType === CourseType.Webinar);
    const conferences = serverRecommended.filter((course) => course.courseType === CourseType.Conference);

    const mixed: Course[] = [];
    while (mixed.length < MAX_RECOMMENDED) {
      let added = false;

      if (onDemand.length > 0 && mixed.length < MAX_RECOMMENDED) {
        mixed.push(onDemand.shift()!);
        added = true;
      }
      if (webinars.length > 0 && mixed.length < MAX_RECOMMENDED) {
        mixed.push(webinars.shift()!);
        added = true;
      }
      if (conferences.length > 0 && mixed.length < MAX_RECOMMENDED) {
        mixed.push(conferences.shift()!);
        added = true;
      }

      if (!added) {
        break;
      }
    }

    return mixed;
  }, [serverRecommended, recommendedTypeFilter]);


  const DATE_PRESETS: { value: 'today' | 'tomorrow' | 'next-week'; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'next-week', label: 'Next Week' },
  ];

  // Cross-filtered counts: each dimension's count reflects the OTHER
  // dimension's current selection, so users can preview the result of a
  // click before making it.
  const myCoursesCounts = useMemo(() => {
    const matchesType = (reg: Registration, t: TypeFilter) =>
      t === 'all' ||
      (t === 'ondemand' && reg.courseType === CourseType.OnDemand) ||
      (t === 'webinar' && reg.courseType === CourseType.Webinar) ||
      (t === 'conference' && reg.courseType === CourseType.Conference);
    const matchesStatus = (reg: Registration, s: MyCoursesStatusFilter) =>
      s === 'all' ||
      (s === 'active' && isRegistrationActive(reg)) ||

      (s === 'completed' && reg.status === 'completed');
    const typeCount = (t: TypeFilter) =>
      registrations.filter(r => matchesType(r, t) && matchesStatus(r, myCoursesStatusFilter)).length;
    const statusCount = (s: MyCoursesStatusFilter) =>
      registrations.filter(r => matchesStatus(r, s) && matchesType(r, myCoursesTypeFilter)).length;
    return {
      type: {
        all: typeCount('all'),
        ondemand: typeCount('ondemand'),
        webinar: typeCount('webinar'),
        conference: typeCount('conference'),
      },
      status: {
        active: statusCount('active'),
        completed: statusCount('completed'),
        all: statusCount('all'),
      },
    };
  }, [registrations, myCoursesTypeFilter, myCoursesStatusFilter]);

  const filteredRegistrations = useMemo(() => {
    return registrations
      .filter(reg => {
        const matchesType =
          myCoursesTypeFilter === 'all' ||
          (myCoursesTypeFilter === 'ondemand' && reg.courseType === CourseType.OnDemand) ||
          (myCoursesTypeFilter === 'webinar' && reg.courseType === CourseType.Webinar) ||
          (myCoursesTypeFilter === 'conference' && reg.courseType === CourseType.Conference);
        const matchesStatus =
          myCoursesStatusFilter === 'all' ||
          (myCoursesStatusFilter === 'active' && isRegistrationActive(reg)) ||
          (myCoursesStatusFilter === 'completed' && reg.status === 'completed');
        return matchesType && matchesStatus;
      })
      .sort((a, b) => getRegistrationSortKey(a) - getRegistrationSortKey(b));
  }, [registrations, myCoursesTypeFilter, myCoursesStatusFilter]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
          Courses
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse the catalog, continue learning, and register for upcoming live sessions.
        </Typography>
      </Box>

      {/* My Courses */}
      {registrations.length > 0 && (
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VideoLibraryIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              <Typography
                variant="h6"
                sx={{ color: 'primary.main', fontWeight: 500, fontSize: '20px', letterSpacing: '0.15px' }}
              >
                My Courses
              </Typography>
            </Box>

            {/* Filter controls — two grouped segmented pill controls.
                Side-by-side on wide screens, stacked when the card is narrow. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 2.5 },
                mb: 2,
                rowGap: 1.5,
              }}
            >
              <SegmentedFilter<TypeFilter>
                label="Format"
                value={myCoursesTypeFilter}
                options={[
                  { value: 'all', label: 'All', count: myCoursesCounts.type.all },
                  { value: 'ondemand', label: 'On-Demand', count: myCoursesCounts.type.ondemand },
                  { value: 'webinar', label: 'Webinar', count: myCoursesCounts.type.webinar },
                  { value: 'conference', label: 'Conference', count: myCoursesCounts.type.conference },
                ]}
                onChange={(v) => { setMyCoursesTypeFilter(v); setShowAllMyCourses(false); }}
              />
              <SegmentedFilter<MyCoursesStatusFilter>
                label="Status"
                value={myCoursesStatusFilter}
                options={[
                  { value: 'active', label: 'Active', count: myCoursesCounts.status.active },
                  { value: 'completed', label: 'Completed', count: myCoursesCounts.status.completed },
                  { value: 'all', label: 'All', count: myCoursesCounts.status.all },
                ]}
                onChange={(v) => { setMyCoursesStatusFilter(v); setShowAllMyCourses(false); }}
              />
            </Box>

            {/* Registered courses list */}
            {filteredRegistrations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No courses match your filters.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.125 }}>
                {(showAllMyCourses
                  ? filteredRegistrations
                  : filteredRegistrations.slice(0, MY_COURSES_INITIAL_VISIBLE)
                ).map((reg, idx) => {
                  const isWebinar = reg.courseType === CourseType.Webinar;
                  const isConference = reg.courseType === CourseType.Conference;
                  const isWebinarType = isWebinar || isConference;
                  const launchUrl = isWebinarType ? reg.joinUrl : reg.courseUrl;
                  const detailsPath = getRegistrationDetailsPath(reg);
                  return (
                    <Box key={reg.id}>
                      {idx > 0 && <Divider />}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.875, flexWrap: 'wrap' }}>
                        {/* Title */}
                        <Typography
                          component={Link}
                          to={detailsPath}
                          sx={{
                            flex: 1,
                            minWidth: 180,
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: 1.4,
                            textDecoration: 'none',
                            color: 'text.primary',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {reg.courseTitle}
                        </Typography>

                        {/* Type chip */}
                        <Box
                          component="span"
                          sx={(theme) => {
                            const token = isConference
                              ? theme.palette.status.conference
                              : isWebinar
                                ? theme.palette.status.webinar
                                : theme.palette.status.onDemand;
                            return {
                              display: 'inline-flex',
                              alignItems: 'center',
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              px: 0.875,
                              py: '2px',
                              borderRadius: '4px',
                              bgcolor: token.bg,
                              color: token.fg,
                              flexShrink: 0,
                            };
                          }}
                        >
                          {isConference ? 'Conference' : isWebinar ? 'Webinar' : 'On-Demand'}
                        </Box>

                        {/* Date/time for live sessions */}
                        {isWebinarType && reg.startDate && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                            {formatWebinarInstance(reg.startDate, reg.endDate)}
                          </Typography>
                        )}

                        {/* Status */}
                        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize', flexShrink: 0 }}>
                          {reg.status.replace('_', ' ')}
                        </Typography>

                        {/* Action button */}
                        {launchUrl ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            href={launchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<PlayCircleIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{ textTransform: 'none', fontSize: '14px', fontWeight: 600, flexShrink: 0 }}
                          >
                            {isWebinarType ? 'Join Now' : 'Launch'}
                          </Button>
                        ) : isWebinarType && reg.status !== 'completed' ? (
                          <Typography variant="caption" sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            Opens {reg.startDate
                              ? new Date(parseApiDate(reg.startDate).getTime()).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })
                              : 'soon'}
                          </Typography>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            component={Link}
                            to={detailsPath}
                            endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                            sx={{ textTransform: 'none', fontSize: '14px', fontWeight: 600, flexShrink: 0 }}
                          >
                            Continue
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Show more / less toggle — mirrors MyLearning.tsx pattern */}
            {filteredRegistrations.length > MY_COURSES_INITIAL_VISIBLE && (
              <Box sx={{ pt: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setShowAllMyCourses(prev => !prev)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: 'text.secondary',
                    px: 0,
                    minWidth: 0,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: 'text.primary',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {showAllMyCourses
                    ? 'Show fewer courses'
                    : `Show more courses (${filteredRegistrations.length - MY_COURSES_INITIAL_VISIBLE})`}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recommended Learning */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AutoStoriesIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              variant="h6"
              sx={{ color: 'primary.main', fontWeight: 500, fontSize: '20px', letterSpacing: '0.15px' }}
            >
              Recommended Learning
            </Typography>
            <Button
              size="small"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() =>
                navigate('/app/assistant', {
                  state: { initialMessage: 'Based on what you know about me, what courses should I take?' },
                })
              }
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '100px',
                px: 1.75,
                py: '4px',
                border: '1.5px solid',
                borderColor: 'rgba(86,151,55,0.4)',
                color: 'primary.main',
                bgcolor: 'rgba(86,151,55,0.04)',
                '&:hover': {
                  bgcolor: 'rgba(86,151,55,0.1)',
                  borderColor: 'primary.main',
                },
                transition: 'all 0.16s ease',
              }}
            >
              Ask Assistant
            </Button>
          </Box>

          {/* Format — same segmented pill control as Course Catalog's Format filter. */}
          <Box sx={{ mb: 2 }}>
            <SegmentedFilter<TypeFilter>
              label="Format"
              value={recommendedTypeFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'ondemand', label: 'On-Demand' },
                { value: 'webinar', label: 'Webinars' },
                { value: 'conference', label: 'Conferences' },
              ]}
              onChange={setRecommendedTypeFilter}
              showCounts={false}
            />
          </Box>

          {/* Horizontal scrollable cards */}
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
            {recommendedLoading ? (
              <CircularProgress size={24} />
            ) : recommendedCourses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No courses available.</Typography>
            ) : (
              recommendedCourses.map((course) => {
                const isWebinar = course.courseType === CourseType.Webinar;
                const isConference = course.courseType === CourseType.Conference;

                return (
                  <Card
                    key={course.id}
                    elevation={0}
                    sx={{
                      minWidth: { xs: 260, sm: 228 },
                      maxWidth: { xs: 280, sm: 228 },
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '4px',
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {recommendedTypeFilter === 'all' && (
                        <Box
                          component="span"
                          sx={(theme) => {
                            const token = isConference
                              ? theme.palette.status.conference
                              : isWebinar
                                ? theme.palette.status.webinar
                                : theme.palette.status.onDemand;
                            return {
                              display: 'flex',
                              width: 'fit-content',
                              alignItems: 'center',
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              px: 0.875,
                              py: '2px',
                              borderRadius: '4px',
                              mb: 1,
                              bgcolor: token.bg,
                              color: token.fg,
                            };
                          }}
                        >
                          {isConference ? 'Conference' : isWebinar ? 'Webinar' : 'On-Demand'}
                        </Box>
                      )}

                      <Chip
                        label={formatCreditLabel(course)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 1, borderRadius: '100px', height: '24px', fontSize: '12px' }}
                      />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 500, fontSize: '16px', lineHeight: 1.5, letterSpacing: '0.15px', mb: 0.5 }}
                    >
                      {course.title}
                    </Typography>
                    {course.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1.5, lineHeight: 1.66, letterSpacing: '0.4px' }}
                      >
                        {stripHtml(course.description).slice(0, 90)}
                        {stripHtml(course.description).length > 90 ? '...' : ''}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      component={Link}
                      to={`${course.id}`}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        letterSpacing: '0.46px',
                        fontWeight: 500,
                      }}
                    >
                      View Course
                    </Button>
                  </CardContent>
                </Card>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Course Catalog */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LibraryBooksIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{ color: 'primary.main', fontWeight: 500, fontSize: '20px', letterSpacing: '0.15px' }}
          >
            Course Catalog
          </Typography>
        </Box>

        {/* Search bar — elevated above the filter rows */}
        <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by title, topic, or instructor…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '16px',
                bgcolor: searchQuery ? 'rgba(86,151,55,0.04)' : 'background.paper',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                '& fieldset': { borderColor: searchQuery ? 'primary.main' : 'rgba(0,0,0,0.18)', borderWidth: searchQuery ? '1.5px' : '1px' },
                '&:hover fieldset': { borderColor: 'primary.light' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
                '& input': { py: '10px' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Format — same segmented pill pattern as the My Courses section. */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <SegmentedFilter<TypeFilter>
            label="Format"
            value={typeFilter}
            options={[
              { value: 'all', label: 'All', count: formatCounts.all },
              { value: 'ondemand', label: 'On-Demand', count: formatCounts.ondemand },
              { value: 'webinar', label: 'Webinars', count: formatCounts.webinar },
              { value: 'conference', label: 'Conferences', count: formatCounts.conference },
            ]}
            onChange={setTypeFilter}
          />
        </Box>

        {/* Dropdowns */}
        <Box sx={{ px: 2, py: 1.75, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 188 } }}>
            <InputLabel sx={{ fontSize: '14px' }}>Field of Study</InputLabel>
            <Select
              value={selectedCategory === 'All' ? '' : selectedCategory}
              label="Field of Study"
              onChange={(e) => setSelectedCategory(e.target.value || 'All')}
              sx={{ fontSize: '14px', borderRadius: '6px' }}
            >
              <MenuItem value="" sx={{ fontSize: '14px' }}>All Fields</MenuItem>
              {categories.filter(c => c !== 'All').map((cat) => (
                <MenuItem key={cat} value={cat} sx={{ fontSize: '14px' }}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 188 } }}>
            <InputLabel sx={{ fontSize: '14px' }}>Instructor</InputLabel>
            <Select
              value={instructorFilter}
              label="Instructor"
              onChange={(e) => setInstructorFilter(e.target.value)}
              sx={{ fontSize: '14px', borderRadius: '6px' }}
            >
              <MenuItem value="" sx={{ fontSize: '14px' }}>All Instructors</MenuItem>
              {instructors.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: '14px' }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Date range — unified white surface. */}
        <Box sx={{ px: 2, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="From"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                const v = e.target.value;
                setDateFrom(v);
                setActiveDatePreset(null);
                if (v && dateTo && v > dateTo) setDateTo(v);
              }}
              sx={{ width: { xs: '100%', sm: 148 }, '& .MuiInputBase-root': { fontSize: '14px', borderRadius: '6px' } }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: dateTo || undefined }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              value={dateTo}
              onChange={(e) => {
                const v = e.target.value;
                setDateTo(v);
                setActiveDatePreset(null);
                if (v && dateFrom && v < dateFrom) setDateFrom(v);
              }}
              sx={{ width: { xs: '100%', sm: 148 }, '& .MuiInputBase-root': { fontSize: '14px', borderRadius: '6px' } }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
            />
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {DATE_PRESETS.map(({ value, label }) => (
                <Chip
                  key={value}
                  label={label}
                  size="small"
                  variant={activeDatePreset === value ? 'filled' : 'outlined'}
                  color={activeDatePreset === value ? 'primary' : 'default'}
                  onClick={() => handleDatePreset(value)}
                  sx={{
                    borderRadius: '4px',
                    cursor: 'pointer',
                    height: '28px',
                    fontSize: '12px',
                    fontWeight: activeDatePreset === value ? 600 : 400,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Credit hours */}
        {availableCreditValues.length > 0 && (
            <Box sx={{ px: 2, py: 1.75 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {availableCreditValues.map((credit) => {
                  const selected = creditFilters.includes(credit);
                  return (
                    <Chip
                      key={credit}
                      label={`${credit} hr${credit !== 1 ? 's' : ''}`}
                      size="small"
                      variant={selected ? 'filled' : 'outlined'}
                      color={selected ? 'primary' : 'default'}
                      onClick={() => toggleCredit(credit)}
                      onDelete={selected ? () => toggleCredit(credit) : undefined}
                      sx={{ borderRadius: '4px', cursor: 'pointer', height: '28px', fontSize: '12px', fontWeight: selected ? 600 : 400 }}
                    />
                  );
                })}
              </Box>
            </Box>
        )}

        {/* Footer: results count + reset. Left-grouped so the clear action
            stays adjacent to the filter controls on wide screens instead of
            drifting to the far edge of the card. */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 2, minHeight: 44 }}>
          <Typography sx={{ fontSize: '12px', color: 'text.disabled', fontWeight: 500 }}>
            {interleavedCourses.length} result{interleavedCourses.length !== 1 ? 's' : ''}
          </Typography>
          {activeFilterCount > 0 && (
            <Button
              variant="text"
              size="small"
              onClick={handleResetFilters}
              startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                fontSize: '12px',
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 600,
                py: 0.25,
                px: 1,
                minWidth: 0,
                '&:hover': { bgcolor: 'rgba(86,151,55,0.08)' },
              }}
            >
              Clear filters ({activeFilterCount})
            </Button>
          )}
        </Box>

        <Divider />

        {/* Date filter notice */}
        {(dateFrom || dateTo) && (
          <Alert severity="info" sx={{ mx: 2, mt: 2, fontSize: '13px' }}>
            Date Filters Applied - Displaying Live Sessions (Webinars and Conferences)
          </Alert>
        )}

        {/* Course List — lives inside the same card as the filters above it,
            so results always read as directly connected to the controls that produced them. */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredCourses.length > 0 ? (
          <Box>
          {visibleCourses.map((course, idx) => {
            const isWebinar = course.courseType === CourseType.Webinar;
            const isConference = course.courseType === CourseType.Conference;
            const isWebinarType = isWebinar || isConference;
            const upcomingInstances = course.upcomingInstances ?? [];
            // Chips show only instances inside the active date range; the
            // "View all" link still counts every eligible instance because it
            // navigates to the detail page, which is unfiltered.
            const eligibleUpcomingInstances = getEligibleWebinarInstances(upcomingInstances);
            const visibleInstances = getEligibleWebinarInstances(upcomingInstances, dateRange.from, dateRange.to).slice(0, 3);
            const hasMoreInstances = eligibleUpcomingInstances.length > 3;

            return (
              <Box key={course.id}>
                {idx > 0 && <Divider />}
                <Box sx={{ px: 2.5, py: 2 }}>
                  {/* Title + action row */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ pt: '2px' }}>
                      <BookmarkButton courseId={course.id} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Format badge — colors come from theme.palette.status so
                          live/on-demand identity stays consistent across pages. */}
                      <Box
                        component="span"
                        sx={(theme) => {
                          const token = isConference
                            ? theme.palette.status.conference
                            : isWebinar
                              ? theme.palette.status.webinar
                              : theme.palette.status.onDemand;
                          return {
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            px: 0.875,
                            py: '2px',
                            borderRadius: '4px',
                            mr: 1,
                            verticalAlign: 'middle',
                            bgcolor: token.bg,
                            color: token.fg,
                          };
                        }}
                      >
                        {isConference ? 'Conference' : isWebinar ? 'Webinar' : 'On-Demand'}
                      </Box>

                      {/* Title */}
                      <Typography
                        component={Link}
                        to={`${course.id}`}
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: 1.5,
                          textDecoration: 'none',
                          color: 'text.primary',
                          verticalAlign: 'middle',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        {course.title}
                      </Typography>

                      {/* Meta line */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {(() => {
                          const fos = getFieldsOfStudy(course)[0];
                          if (!fos) return null;
                          const Icon = getCategoryIcon(fos);
                          return (
                            <>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375, fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
                                <Icon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                {fos}
                              </Box>
                              <Box component="span" sx={{ fontSize: '12px', color: 'text.disabled', lineHeight: 1 }}>·</Box>
                            </>
                          );
                        })()}
                        <Box component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>
                          {formatCreditLabel(course)}
                        </Box>
                        {course.level && (
                          <>
                            <Box component="span" sx={{ fontSize: '12px', color: 'text.disabled', lineHeight: 1 }}>·</Box>
                            <Box component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                              {course.level}
                            </Box>
                          </>
                        )}
                      </Box>

                      {/* Instructor */}
                      {course.speakers && course.speakers.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.375 }}>
                          <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography sx={{ fontSize: '12px', color: 'text.disabled', fontStyle: 'italic' }}>
                            {course.speakers.map(s => (s.displayName || s.name || '').trim()).filter(Boolean).join(', ')}
                          </Typography>
                        </Box>
                      )}

                      {/* Live-course upcoming sessions */}
                      {isWebinarType && visibleInstances.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.375 }}>
                          {visibleInstances.map((instance, i) => (
                            <Box
                              key={i}
                              component={Link}
                              to={`${course.id}?instanceId=${instance.id}`}
                              sx={(theme) => {
                                const token = isConference
                                  ? theme.palette.status.conference
                                  : theme.palette.status.webinar;
                                const webinarBorder = darken(token.bg, 0.5);
                                return {
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  textDecoration: 'none',
                                  width: 'fit-content',
                                  px: 0.875,
                                  py: 0.25,
                                  borderRadius: '4px',
                                  border: '1px solid',
                                  borderColor: webinarBorder,
                                  bgcolor: token.bg,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: token.fg,
                                    bgcolor: token.bg,
                                    transform: 'translateX(2px)',
                                  },
                                };
                              }}
                            >
                              <CalendarTodayIcon
                                sx={(theme) => ({
                                  fontSize: 11,
                                  color: isConference ? theme.palette.status.conference.fg : theme.palette.status.webinar.fg,
                                  flexShrink: 0,
                                })}
                              />
                              <Typography
                                sx={(theme) => ({
                                  fontSize: '12px',
                                  color: isConference ? theme.palette.status.conference.fg : theme.palette.status.webinar.fg,
                                  fontWeight: 500,
                                })}
                              >
                                {formatWebinarInstance(instance.startDate, instance.endDate)}
                              </Typography>
                            </Box>
                          ))}
                          <Typography
                            component={Link}
                            to={`${course.id}`}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.25,
                              fontSize: '12px',
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontWeight: 500,
                              mt: 0.25,
                              width: 'fit-content',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {hasMoreInstances
                              ? `View all ${eligibleUpcomingInstances.length} available times →`
                              : 'View all available times →'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flexShrink: 0, pt: '2px' }}>
                      {renderEnrollButton(course)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
          {visibleCourses.length < filteredCourses.length && (
            <Box ref={setSentinelEl} sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <CircularProgress size={24} />
            </Box>
          )}
          </Box>
        ) : (
          <Box sx={{ px: 4, py: 6 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', mb: 0.75, color: 'text.primary' }}>
              No courses match these filters
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: '52ch', lineHeight: 1.6 }}>
              {searchQuery
                ? `No results for "${searchQuery}". Try a broader search term, or clear all filters to browse the full catalog.`
                : 'Remove a category, credit hour, or date range to see more of the catalog.'}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '100px',
                px: 2.5,
              }}
            >
              Clear all filters
            </Button>
          </Box>
        )}
      </Card>

      {/* Request Access Dialog */}
      <Dialog open={!!requestDialog} onClose={() => setRequestDialog(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold" component="span">
            Request Module Access
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {requestError && <Alert severity="error">{requestError}</Alert>}
            {requestSuccess && <Alert severity="success">{requestSuccess}</Alert>}
            {!requestSuccess && (
              <>
                <Typography variant="body2">
                  You need access to the{' '}
                  <strong>
                    {requestDialog?.addonId
                      ? (ADDON_DISPLAY_NAMES[requestDialog.addonId] ?? requestDialog.addonId)
                      : requestDialog?.courseType === 0 ? 'On-Demand' : 'Webinar'}
                  </strong>{' '}
                  module to enroll in this course. Submit a request to your tenant admin.
                </Typography>
                <TextField
                  label="Note (optional)"
                  placeholder="Add context to help your admin process this request…"
                  multiline
                  rows={3}
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  disabled={requestLoading}
                  fullWidth
                  size="small"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRequestDialog(null)} variant="outlined" disabled={requestLoading}>
            Cancel
          </Button>
          {!requestSuccess && (
            <Button
              onClick={handleRequestAccess}
              variant="contained"
              disabled={requestLoading}
            >
              {requestLoading ? <CircularProgress size={20} /> : 'Submit Request'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
